import { useCallback } from 'react';

import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { QueryPagination, createQueryOptions } from '../../../query';
import { RequestMeta } from '../../temporary/requestmeta.types';
import { storeItemWithCrowdfundingType } from '../../temporary/storeItemWithCrowdfundingType';
import { useStoreApi } from '../api';
import { storeKeys } from '../query-key-factory';
import { StoreItem, StoreItemResponse, StoreItemsResponse } from '../types';
import { DynamicCampaignStoreItemOrAthon } from '../types/utils';
import { createCauseQuery } from '../utils';
import { GetStoreItemsMeta, StoreItemSearch } from './types/search.types';
import { StoreItemOptionsResponse } from './types/types';

const useGetStoreItems = <T extends StoreItem = StoreItem>() => {
  const queryClient = useQueryClient();
  const StoreApi = useStoreApi();

  return async (context: { meta?: GetStoreItemsMeta }) => {
    const { queryString, causeId } = context.meta || {};
    const { data } = await StoreApi.get<StoreItemsResponse<T[]>>(`/store_items?${queryString}`);

    data.store_items.forEach((item) => {
      /**
       * The view from list is not the same as the view show on membership so we do not want to pre-seed this data
       */
      if (item.type === 'StoreItems::Membership') {
        return;
      }

      if (causeId) {
        const key = storeKeys.id(causeId).items.id(item.uuid).detail;
        queryClient.setQueryData(key, storeItemWithCrowdfundingType(item));
      }
    });

    return { ...data, store_items: data.store_items.map(storeItemWithCrowdfundingType) };
  };
};

const useShowStoreItem = <T = StoreItem>({
  causeId,
  uuid,
  select,
  _enabled = true,
}: {
  causeId?: string;
  uuid?: string;
  _enabled?: boolean;
  select?: (data: StoreItem) => T;
}) => {
  const StoreApi = useStoreApi();
  const causeQuery = createCauseQuery(causeId);

  const getItem = async () => {
    // Throw to enable type safety because we know the query will not run unless uuid is defined
    if (!uuid || !causeId) throw new Error('uuid is required');
    const { data } = await StoreApi.get<StoreItemResponse<StoreItem>>(
      `/store_items/${uuid}?${causeQuery}`
    );

    return storeItemWithCrowdfundingType(data.store_item);
  };

  const queryKey = uuid && causeId ? storeKeys.id(causeId).items.id(uuid).detail : [];

  return useQuery({
    queryKey,
    queryFn: getItem,
    enabled: !!uuid && !!causeId && _enabled,
    select,
  });
};

const useMakeListStoreItemsQueryObject = <T extends StoreItem = StoreItem>({
  pagination,
  queryOptions,
  _enabled = true,
}: {
  pagination?: QueryPagination;
  queryOptions?: StoreItemSearch;
  _enabled?: boolean;
}) => {
  const getStoreItems = useGetStoreItems<T>();

  const makeListStoreItemsQueryObject = useCallback(
    (causeId: string) => {
      const causeQuery = createCauseQuery(causeId);

      const { queryString, queryObject, queryPagination } = createQueryOptions<StoreItemSearch>({
        search: queryOptions,
        pagination,
        existingSearchString: causeQuery,
      });
      const queryKey = causeId ? storeKeys.id(causeId).items.all : [];

      return {
        queryKey: [...queryKey, queryObject, queryPagination],
        queryFn: getStoreItems,
        enabled: !!causeId && _enabled,
        meta: { queryString, causeId },
        placeholderData: keepPreviousData,
      };
    },
    [queryOptions, pagination, getStoreItems, _enabled]
  );

  return { makeListStoreItemsQueryObject };
};

const useListStoreItems = <T extends StoreItem, U = StoreItemsResponse<T[]>>({
  causeId,
  select,
  pagination,
  queryOptions,
  _enabled = true,
}: {
  causeId?: string;
  pagination?: QueryPagination;
  queryOptions?: StoreItemSearch;
  select?: (data: StoreItemsResponse<DynamicCampaignStoreItemOrAthon<T>[]>) => U;
  _enabled?: boolean;
}) => {
  const getStoreItems = useGetStoreItems<T>();
  const causeQuery = createCauseQuery(causeId);
  const { queryString, queryObject, queryPagination } = createQueryOptions<StoreItemSearch>({
    search: queryOptions,
    pagination,
    existingSearchString: causeQuery,
  });
  const queryKey = causeId ? storeKeys.id(causeId).items.all : [];

  return useQuery({
    queryKey: [...queryKey, queryObject, queryPagination],
    queryFn: getStoreItems,
    enabled: !!causeId && _enabled,
    meta: { queryString, causeId },
    select,
    placeholderData: keepPreviousData,
  });
};

const useInfiniteListStoreItems = ({
  causeId,
  queryOptions,
  _enabled = true,
}: {
  causeId?: string;
  queryOptions?: StoreItemSearch;
  _enabled?: boolean;
}) => {
  const getStoreItems = useGetStoreItems();
  const causeQuery = createCauseQuery(causeId);
  const infiniteData = async ({ pageParam }: { pageParam: { limit: number; offset: number } }) => {
    const { queryString } = createQueryOptions<StoreItemSearch>({
      search: queryOptions,
      pagination: {
        limit: pageParam.limit,
        offset: pageParam.offset,
      },
      existingSearchString: causeQuery,
    });
    const result = await getStoreItems({
      meta: { queryString, causeId },
    });
    return result;
  };
  const queryKey = causeId ? storeKeys.id(causeId).items.all : [];

  return useInfiniteQuery({
    queryKey: [...queryKey, 'export', queryOptions],
    queryFn: infiniteData,
    enabled: !!causeId && _enabled,
    initialPageParam: { limit: 24, offset: 0 },
    getNextPageParam: (lastPage) => {
      if (!lastPage) return undefined;
      const { meta } = lastPage as { meta: RequestMeta };
      if (!meta) return undefined;
      const { has_more, limit, offset } = meta;
      if (!has_more) {
        return undefined;
      }
      return { limit, offset: offset + limit };
    },
  });
};

const selectTotal = (data?: StoreItemsResponse<StoreItem[]>) => data?.meta.total;
/** Returns the count of the store items based on the given queryOptions */
const useStoreItemCount = ({
  causeId,
  queryOptions,
  _enabled = true,
}: {
  causeId?: string;
  queryOptions?: StoreItemSearch;
  _enabled?: boolean;
}) => {
  return useListStoreItems({
    causeId: causeId,
    queryOptions: {
      ...queryOptions,
      join: 'AND',
      options: {
        total: true,
      },
    },
    select: selectTotal,
    _enabled,
  });
};
function useShowDonationItem<T = StoreItem>({
  storeId,
  select,
}: {
  storeId?: string;
  select?: (data: StoreItem) => T;
}) {
  const StoreApi = useStoreApi();
  const getDonationStoreItem = async (): Promise<StoreItem> => {
    const { data } = await StoreApi.get(`/store_items/donations?cause_id=${storeId}`);
    return data.store_item;
  };

  return useQuery({
    queryKey: ['cause', storeId, 'store', 'item', 'donation'],
    queryFn: getDonationStoreItem,
    enabled: !!storeId,
    select,
  });
}

const useListStoreItemOptions = <T = StoreItemOptionsResponse>({
  causeId,
  select,
}: {
  causeId?: string;
  select?: (data: StoreItemOptionsResponse) => T;
}) => {
  const StoreApi = useStoreApi();
  const getStoreItemOptions = async (): Promise<StoreItemOptionsResponse> => {
    const { data } = await StoreApi.get(`/store_items/options?cause_id=${causeId}`);
    return data;
  };

  const key = causeId ? storeKeys.id(causeId).items.options : [];

  return useQuery({
    queryKey: key,
    queryFn: getStoreItemOptions,
    enabled: !!causeId,
    select,
  });
};

export {
  useInfiniteListStoreItems,
  useListStoreItemOptions,
  useListStoreItems,
  useMakeListStoreItemsQueryObject,
  useShowDonationItem,
  useShowStoreItem,
  useStoreItemCount,
};
